// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, ShoppingCart } from 'lucide-react';
import { DEFAULT_MEDIA_FIELDS } from '@/types/events';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { formatUsPhone, phoneDigits } from "@/lib/phone";
import { useAuth } from '@/contexts/AuthContext';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
// 16px inputs stop iOS zoom; 44px keeps every target thumb-sized.
const CTRL = 'min-h-[44px] text-base md:text-sm';


interface Props {
  eventId?: string;
  eventName?: string;
  notificationEmail?: string;
  mediaCredentialsPrice?: number; // dollars
  onSuccess?: () => void;
  // Legacy/unused:
  mediaCredentialsFormId?: string;
}

type FieldConfig = {
  key: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  helper_text?: string;
  options?: string[];
  order: number;
};

const fmt = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;

const MediaCredentialsForm: React.FC<Props> = ({
  eventId,
  eventName,
  mediaCredentialsPrice = 0,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [fields, setFields] = useState<FieldConfig[]>([]);
  const [formTitle, setFormTitle] = useState<string | null>(null);
  const [formDescription, setFormDescription] = useState<string | null>(null);
  const [responses, setResponses] = useState<Record<string, any>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      if (!eventId) {
        setFields(DEFAULT_MEDIA_FIELDS as any);
        setLoading(false);
        return;
      }
      const { data: cfg } = await supabase
        .from('event_form_configs')
        .select('fields, form_title, form_description')
        .eq('event_id', eventId)
        .eq('form_type', 'media_credentials')
        .maybeSingle();
      if (cancelled) return;

      const list: FieldConfig[] =
        (cfg?.fields as FieldConfig[])?.length ? (cfg.fields as FieldConfig[]) : (DEFAULT_MEDIA_FIELDS as any);
      list.sort((a, b) => (a.order || 0) - (b.order || 0));
      setFields(list);
      setFormTitle(cfg?.form_title ?? null);
      setFormDescription(cfg?.form_description ?? null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  // Signed-in visitors get a convenience prefill only — nothing is required.
  useEffect(() => {
    if (!user) return;
    const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim();
    setResponses((prev) => {
      const next = { ...prev };
      if (!next.email && user.email) next.email = user.email;
      if (fullName) {
        if (!next.name && !next.full_name) next.name = fullName;
      }
      return next;
    });
  }, [user?.id, profile?.first_name, profile?.last_name]);

  const setVal = (key: string, value: any) =>
    setResponses((p) => ({ ...p, [key]: value }));

  const totalDollars = mediaCredentialsPrice || 0;

  const renderField = (f: FieldConfig) => {
    const v = responses[f.key];
    switch (f.type) {
      case 'textarea':
        return <Textarea className="text-base md:text-sm" value={v ?? ''} placeholder={f.placeholder} rows={3} onChange={(e) => setVal(f.key, e.target.value)} />;
      case 'select':
        return (
          <Select value={v ?? ''} onValueChange={(val) => setVal(f.key, val)}>
            <SelectTrigger className={CTRL}><SelectValue placeholder={f.placeholder || 'Select…'} /></SelectTrigger>
            <SelectContent>
              {(f.options ?? []).map((opt) => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'multi_select': {
        const arr: string[] = Array.isArray(v) ? v : [];
        return (
          <div className="grid grid-cols-2 gap-2">
            {(f.options ?? []).map((opt) => {
              const checked = arr.includes(opt);
              return (
                <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(c) => {
                      const next = c ? [...arr, opt] : arr.filter((x) => x !== opt);
                      setVal(f.key, next);
                    }}
                  />
                  {opt}
                </label>
              );
            })}
          </div>
        );
      }
      case 'checkbox':
        return (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={!!v} onCheckedChange={(c) => setVal(f.key, !!c)} />
            {f.placeholder || f.label}
          </label>
        );
      case 'email':
        return <Input className={CTRL} type="email" inputMode="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" value={v ?? ''} placeholder={f.placeholder} onChange={(e) => setVal(f.key, e.target.value)} />;
      case 'phone':
        return (
          <Input
            className={CTRL}
            type="tel"
            inputMode="tel"
            value={formatUsPhone(v ?? '')}
            placeholder={f.placeholder || '(816) 223-7452'}
            onChange={(e) => setVal(f.key, phoneDigits(e.target.value))}
          />
        );

      case 'number':
        return <Input className={CTRL} type="number" value={v ?? ''} placeholder={f.placeholder} onChange={(e) => setVal(f.key, e.target.value)} />;
      default:
        return <Input className={CTRL} value={v ?? ''} placeholder={f.placeholder} onChange={(e) => setVal(f.key, e.target.value)} />;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    for (const f of fields) {
      if (!f.required) continue;
      const v = responses[f.key];
      const empty = v == null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && v.length === 0);
      if (empty) {
        toast.error(`${f.label} is required`);
        return;
      }
    }

    const emailValue = String(responses.email || '').trim();
    if (!EMAIL_RE.test(emailValue)) {
      toast.error('Enter a valid email address — approvals are sent there.');
      return;
    }

    setSubmitting(true);
    try {
      const requesterName = String(responses.name || responses.full_name || '').trim();
      const requesterEmail = String(responses.email || '').trim();
      const requesterOrganization = String(
        responses.organization || responses.publication || responses.company || ''
      ).trim();

      const { data, error } = await invokeEdgeFunction('create-media-credentials-checkout', {
        body: {
          eventId,
          amount: Math.round(totalDollars * 100),
          requesterEmail,
          requesterName,
          requesterOrganization,
          formData: responses,
        },
      });
      if (error) throw error;

      if (data?.success) {
        toast.success(data.message || 'Media credentials request submitted!');
        onSuccess?.();
        navigate(`/event-media-credentials?event_id=${eventId}&submitted=true`, {
          state: {
            credential: data.credential ?? {
              name: requesterName,
              email: requesterEmail,
              organization: requesterOrganization,
              phone: String(responses.phone ?? ''),
              coverage_type: String(responses.coverage_type ?? ''),
            },
          },
        });
        return;
      }
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error('We could not submit your request. Please try again.');

    } catch (err: any) {
      toast.error(err?.message || 'Error submitting request.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {(formTitle || formDescription) && (
        <div className="space-y-1">
          {formTitle && <h3 className="text-lg font-semibold">{formTitle}</h3>}
          {formDescription && (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{formDescription}</p>
          )}
        </div>
      )}

      {totalDollars > 0 && (
        <Card className="border-cyan/30 bg-cyan/5">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <ShoppingCart className="h-4 w-4" /> Order Summary
            </div>
            <ul className="text-sm space-y-1">
              <li className="flex justify-between">
                <span>Media Credentials{eventName ? ` — ${eventName}` : ''}</span>
                <span>{fmt(totalDollars)}</span>
              </li>
            </ul>
            <div className="border-t pt-2 flex justify-between font-semibold">
              <span>Total</span>
              <span>{fmt(totalDollars)}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {fields.map((f) => (
        <div key={f.key} className="space-y-1.5">
          <Label htmlFor={f.key}>
            {f.label}
            {f.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          {renderField(f)}
          {f.helper_text && <p className="text-xs text-muted-foreground">{f.helper_text}</p>}
        </div>
      ))}

      <div className="sticky bottom-0 -mx-6 mt-2 border-t border-stroke-subtle bg-surface-card px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:static md:mx-0 md:border-0 md:bg-transparent md:p-0">
      <Button type="submit" disabled={submitting} className="w-full min-h-[44px] text-base md:text-sm">
        {submitting ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…</>
        ) : totalDollars > 0 ? (
          `Submit & Pay ${fmt(totalDollars)}`
        ) : (
          'Submit Request'
        )}
      </Button>
      </div>
    </form>
  );
};

export default MediaCredentialsForm;
